<template>
	<div class="kanban-column">
		<div class="column-container elevation-2 round-card background-2" :style="{ '--column-color': column.color }">
			<!-- Column Header -->
			<div class="column-header pa-3">
				<div class="d-flex align-center justify-space-between">
					<span class="column-title font-weight-medium">{{ column.label }}</span>
					<v-chip small :color="column.color" dark>{{ column.count }}</v-chip>
				</div>
			</div>

			<v-divider></v-divider>

			<!-- Column Content -->
			<div class="column-content background-1 pa-2 pb-1">
				<draggable
					v-model="localOpportunities"
					:group="{ name: 'opportunities' }"
					:animation="200"
					ghost-class="kanban-ghost"
					chosen-class="kanban-chosen"
					drag-class="kanban-drag"
					@add="onItemAdded"
					class="draggable-container"
				>
					<opportunity-card
						v-for="opp in localOpportunities"
						:key="opp.id"
						:opportunity="opp"
						@click="$emit('open-opportunity', opp.id)"
					/>
				</draggable>

				<!-- Empty state -->
				<div v-if="!localOpportunities.length" class="empty-state text-center py-4">
					<v-icon color="grey lighten-1" size="32">mdi-clipboard-text-outline</v-icon>
					<div class="text-caption grey--text mt-2">{{ $trans('no_opportunities_found') }}</div>
				</div>
			</div>

			<!-- Load More Button -->
			<div v-if="hasMore" class="column-footer pa-2 text-center background-2">
				<v-btn text small color="primary" @click="loadMore" :loading="loading">
					Carica altre ({{ remaining }})
				</v-btn>
			</div>
		</div>
	</div>
</template>

<script>
import Draggable from 'vuedraggable'
import OpportunityCard from './OpportunityCard.vue'

export default {
	name: 'OpportunityColumn',

	components: {
		Draggable,
		OpportunityCard
	},

	props: {
		column: {
			type: Object,
			required: true
		},
		filters: {
			type: Object,
			default: () => ({})
		}
	},

	data() {
		return {
			localOpportunities: [],
			loading: false
		}
	},

	computed: {
		hasMore() {
			return this.localOpportunities.length < this.column.count
		},

		remaining() {
			return this.column.count - this.localOpportunities.length
		}
	},

	watch: {
		'column.opportunities': {
			immediate: true,
			handler(newVal) {
				this.localOpportunities = newVal ? [...newVal] : []
			}
		}
	},

	methods: {
		onItemAdded(evt) {
			// This fires on the DESTINATION column when an item is dropped here
			const movedOpp = this.localOpportunities[evt.newIndex]
			if (movedOpp) {
				// Salva lo status originale per l'update dei contatori
				const oldStatus = movedOpp.status
				// Aggiorna lo status localmente per l'update ottimistico
				movedOpp.status = this.column.status

				this.$emit('status-changed', {
					opportunityId: movedOpp.id,
					newStatus: this.column.status,
					oldStatus: oldStatus,
					opportunity: movedOpp
				})
			}
		},

		async loadMore() {
			this.loading = true
			try {
				const response = await this.axios.post('opportunities-kanban/load-more', {
					status: this.column.status,
					offset: this.localOpportunities.length,
					...this.filters
				})

				if (response.data && response.data.opportunities) {
					this.localOpportunities.push(...response.data.opportunities)
				}
			} catch (error) {
				this.$eventBusUtility.$emit('errorHandler', error)
			} finally {
				this.loading = false
			}
		},

		// Method to add opportunity when dragged from another column
		addOpportunity(opportunity) {
			this.localOpportunities.unshift(opportunity)
		},

		// Method to remove opportunity when dragged to another column
		removeOpportunity(opportunityId) {
			const index = this.localOpportunities.findIndex(o => o.id === opportunityId)
			if (index > -1) {
				this.localOpportunities.splice(index, 1)
			}
		}
	}
}
</script>

<style scoped>
.kanban-column {
	width: 320px;
	min-width: 320px;
	max-width: 320px;
	margin-right: 16px;
	flex-shrink: 0;
}

.column-container {
	display: flex;
	flex-direction: column;
	max-height: calc(100vh - 200px);
	overflow: hidden;
}

.column-header {
	flex-shrink: 0;
}

.column-content {
	flex: 1;
	overflow-y: auto;
	min-height: 100px;
}

.draggable-container {
	min-height: 50px;
}

.kanban-chosen {
	cursor: grabbing;
}

.empty-state {
	opacity: 0.6;
}

.column-footer {
	box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.08);
}
</style>

<style>
.v-application .column-container.background-2 {
	border-top: 4px solid var(--column-color) !important;
}

.v-application .kanban-drag.opportunity-card {
	opacity: 0.95;
	transform: rotate(2deg) scale(1.02);
	box-shadow: 0 12px 24px rgba(0, 0, 0, 0.25) !important;
	z-index: 9999;
}

.v-application .kanban-ghost.opportunity-card {
	opacity: 1;
	background: rgba(0, 0, 0, 0.04) !important;
	border: 2px dashed rgba(0, 0, 0, 0.15) !important;
	border-color: rgba(0, 0, 0, 0.15) !important;
	border-radius: 15px;
	box-shadow: none !important;
}

.v-application .kanban-ghost.opportunity-card > * {
	opacity: 0;
}
</style>
